import { motion } from 'framer-motion';
import { Compass, Sparkles, Camera, Leaf, ArrowRight, Mail, Lock, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/I18nContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import CinematicBackground from '../components/CinematicBackground';
import WildQuestLogo from '../components/WildQuestLogo';
import { trackRegistration } from '../lib/metaPixel';
import { toast } from 'sonner';
import { landingRouteFor, isAdminUser } from '../lib/routing';
import {
  SeasonalBanner, LiveCommunityFeed, WorldFirstCard,
  PlatformStats, FeatureHighlights, PremiumCta,
} from '../components/LandingBlocks';
import ExplainerHero from '../components/ExplainerHero';

function formatDetail(detail) {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || String(e)).join(' ');
  return detail?.msg || String(detail);
}

export default function Login() {
  const { user, checkAuth } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const [mode, setMode] = useState(null); // null | 'signin' | 'signup'
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const formRef = useRef(null);

  useEffect(() => { if (user) nav('/dashboard', { replace: true }); }, [user, nav]);

  // Scroll email form into view on mobile when opened, and focus first field
  useEffect(() => {
    if (!mode) return;
    const t = setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const first = formRef.current?.querySelector('input');
      first?.focus?.();
    }, 60);
    return () => clearTimeout(t);
  }, [mode]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setBusy(true);
    try {
      if (mode === 'signup') {
        if (!form.name.trim()) throw { response: { data: { detail: t('landing.nameRequired', 'Name is required.') } } };
        await api.post('/auth/register', {
          email: form.email.trim().toLowerCase(),
          password: form.password,
          name: form.name.trim(),
        });
        // Meta Pixel conversion event — no PII sent, method label only.
        trackRegistration({ method: 'email' });
        toast.success(t('landing.welcomeToast', 'Welcome to WildQuest!'));
      } else {
        await api.post('/auth/login', {
          email: form.email.trim().toLowerCase(),
          password: form.password,
          portal: 'player',
        });
        toast.success(t('landing.welcomeBackToast', 'Welcome back'));
      }
      const fresh = await checkAuth();
      const me = fresh || {};
      nav(landingRouteFor(me, isAdminUser(me)), { replace: true });
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || t('landing.signInFailed', 'Sign-in failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#040A08] text-foreground overflow-x-hidden" data-testid="landing-root">
      {/* HERO */}
      <section className="relative min-h-[100svh] w-full flex flex-col justify-between px-6 pt-8 pb-6">
        <CinematicBackground />

        {/* Top bar: brand chip */}
        <motion.div
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="relative z-30 flex items-center justify-between"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass">
            <WildQuestLogo className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-primary-light">{t('landing.brand', 'WildQuest')}</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact />
            <button
              data-testid="btn-scroll-explore"
              onClick={() => document.getElementById('landing-scroll')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
            >
              {t('landing.exploreCta', 'Explore ↓')}
            </button>
          </div>
        </motion.div>

        {/* Center content */}
        <div className="relative z-10 max-w-md mx-auto text-center flex-1 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex justify-center mb-3">
              <WildQuestLogo className="w-24 h-24 sm:w-28 sm:h-28 drop-shadow-2xl"
                              style={{ filter: 'drop-shadow(0 12px 24px rgba(45,106,79,0.45))' }} />
            </div>
            <h1 className="font-serif text-[52px] sm:text-[64px] leading-[0.95] tracking-tight" data-testid="landing-headline">
              {t('landing.headline', 'WildQuest')}
            </h1>
            <p
              className="mt-4 text-sm uppercase tracking-[0.35em] text-accent/95"
              style={{ textShadow: '0 1px 2px rgba(0,0,0,0.45), 0 0 12px rgba(233,196,106,0.25)' }}
              data-testid="landing-tagline"
            >
              {t('landing.tagline', 'Explore Nature · Discover Wildlife · Build Your Legacy')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto mt-10"
          >
            {/* Pulsing halo behind CTA */}
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(233,196,106,0.35) 0%, transparent 60%)',
                filter: 'blur(30px)',
              }}
              animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
            <motion.button
              data-testid="btn-begin-exploring"
              onClick={() => setMode('signup')}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="relative w-64 h-64 rounded-full bg-gradient-to-br from-primary via-primary-light to-primary text-white font-serif text-2xl flex flex-col items-center justify-center gap-2 border-4 border-white/10 shadow-2xl shadow-primary/40"
            >
              <Compass className="w-12 h-12" strokeWidth={1.4} />
              <span>{t('landing.beginExploring', 'Begin Exploring')}</span>
              <span className="text-[10px] uppercase tracking-[0.25em] opacity-80">{t('landing.beginExploringHint', 'One tap to start')}</span>
            </motion.button>
          </motion.div>
        </div>

        {/* Bottom auth actions */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 0.6 }}
          className="relative z-10 max-w-md mx-auto w-full space-y-2"
        >
          <button
            data-testid="btn-google-login"
            onClick={handleGoogle}
            className="w-full py-3.5 rounded-full glass border border-white/10 hover:bg-white/10 flex items-center justify-center gap-3 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            <span className="text-sm">{t('common.continueWithGoogle', 'Continue with Google')}</span>
          </button>

          {!mode ? (
            <button
              data-testid="btn-open-email-auth"
              onClick={() => setMode('signin')}
              className="w-full py-3.5 rounded-full glass border border-white/10 hover:bg-white/10 flex items-center justify-center gap-3 transition-colors"
            >
              <Mail className="w-4 h-4 text-primary-light" />
              <span className="text-sm">{t('common.continueWithEmail', 'Continue with Email')}</span>
            </button>
          ) : (
            <motion.form
              key={mode}
              ref={formRef}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              onSubmit={submitEmail}
              className="space-y-2 p-4 rounded-3xl glass border border-white/10 scroll-mt-6"
              data-testid="email-auth-form"
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] uppercase tracking-[0.25em] text-primary-light">
                  {mode === 'signup' ? t('landing.createYourAccount', 'Create your account') : t('landing.signInWithEmail', 'Sign in with Email')}
                </p>
                <button type="button" onClick={() => setMode(null)} data-testid="btn-email-cancel"
                  className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
              {mode === 'signup' && (
                <input
                  data-testid="input-name"
                  type="text" autoComplete="name" required
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder={t('landing.namePlaceholder', 'Your name')}
                  className="w-full px-4 py-2.5 rounded-full bg-black/30 border border-white/10 text-sm outline-none focus:border-primary-light/60"
                />
              )}
              <input
                data-testid="input-email"
                type="email" autoComplete="email" required
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder={t('landing.emailPlaceholder', 'you@example.com')}
                className="w-full px-4 py-2.5 rounded-full bg-black/30 border border-white/10 text-sm outline-none focus:border-primary-light/60"
              />
              <input
                data-testid="input-password"
                type="password" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} required minLength={8}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder={mode === 'signup' ? t('landing.passwordPlaceholderNew', 'At least 8 characters (letters + numbers)') : t('landing.passwordPlaceholder', 'Password')}
                className="w-full px-4 py-2.5 rounded-full bg-black/30 border border-white/10 text-sm outline-none focus:border-primary-light/60"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid={mode === 'signup' ? 'btn-submit-signup' : 'btn-submit-signin'}
                className="w-full py-3 rounded-full bg-primary text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60 hover:brightness-110 transition"
              >
                {busy ? (
                  <>
                    <span className="w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    <span>{mode === 'signup' ? t('landing.creatingAccount', 'Creating account…') : t('landing.signingIn', 'Signing in…')}</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-3.5 h-3.5" />
                    <span>{mode === 'signup' ? t('landing.createAccount', 'Create account') : t('common.signIn', 'Sign in')}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
              <div className="flex items-center justify-between pt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                <button type="button"
                  data-testid="btn-toggle-signup"
                  onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')}
                  className="hover:text-foreground"
                >{mode === 'signup' ? t('landing.toggleToSignIn', 'Have an account? Sign in') : t('landing.toggleToSignUp', 'New here? Create account')}</button>
                <button type="button"
                  data-testid="btn-forgot-password"
                  onClick={() => nav('/forgot-password')}
                  className="hover:text-foreground"
                >{t('landing.forgotPassword', 'Forgot password?')}</button>
              </div>
            </motion.form>
          )}

          <div className="flex items-center justify-between pt-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t('landing.freeToTry', 'Free to try · No credit card required')}</p>
            <button
              data-testid="btn-admin-signin"
              onClick={() => nav('/admin/login')}
              className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground hover:text-primary-light inline-flex items-center gap-1"
            >
              <ShieldCheck className="w-3 h-3" /> {t('landing.owner', 'Owner')}
            </button>
          </div>
        </motion.div>
      </section>

      {/* SCROLLED CONTENT */}
      <section id="landing-scroll" className="relative max-w-md mx-auto px-6 pt-2 pb-24 space-y-6">
        {/* Cinematic explainer — Sora 2 wildlife + real gameplay so
            visitors understand WildQuest in 60 seconds before signing up. */}
        <ExplainerHero />
        <SeasonalBanner />
        <LiveCommunityFeed />
        <PlatformStats />
        <div>
          <h3 className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">{t('landing.whatAwaitsYou', 'What awaits you')}</h3>
          <FeatureHighlights />
        </div>
        <WorldFirstCard />
        <PremiumCta />

        <motion.button
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          onClick={() => setMode('signup')}
          data-testid="btn-cta-bottom"
          className="w-full py-4 rounded-full bg-primary text-white font-medium flex items-center justify-center gap-2 shadow-lg shadow-primary/30"
        >
          <Camera className="w-4 h-4" /> {t('landing.beginFirstQuest', 'Begin your first quest')}
        </motion.button>
        <p className="text-center text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          {t('landing.loveLetter', 'A love letter to naturalists · Built for the wild in all of us')}
        </p>
      </section>
    </div>
  );
}
